import { t } from "i18next";
import React, { useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { LoaderContext } from "../../AuthContext/AuthContext";
import { useNavigate } from "react-router-dom";

const TermsAndConditionPage = () => {
  const { selectedLanguageID } = useContext(LoaderContext);
  const navigate = useNavigate()
  useEffect(() => {
    navigate('/terms-and-condition')
  }, [selectedLanguageID])
  return (
    <>
      <Helmet>
        <title>Terms and Conditions - Myomnamo - Spiritual Services</title>
        <meta
          name="description"
          content="Read our Terms & Conditions for using Myomnamo services. Understand policies for pandit bookings, pujas, and other spiritual services."
        />

        {/* Canonical Tag */}
        <link
          rel="canonical"
          href="https://www.myomnamo.com/terms-and-condition"
        />

        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Terms and Conditions - Myomnamo - Spiritual Services"
        />
        <meta
          property="og:description"
          content="Read our Terms & Conditions for using Myomnamo services. Understand policies for pandit bookings, pujas, and other spiritual services."
        />
        <meta
          property="og:image"
          content="https://www.myomnamo.com/assets/images/new/logo.png"
        />
        <meta
          property="og:url"
          content="https://www.myomnamo.com/terms-and-condition"
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Myomnamo" />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Terms and Conditions - Myomnamo - Spiritual Services"
        />
        <meta
          name="twitter:description"
          content="Read our Terms & Conditions for using Myomnamo services. Understand policies for pandit bookings, pujas, and other spiritual services."
        />
        <meta
          name="twitter:image"
          content="https://www.myomnamo.com/assets/images/new/logo.png"
        />

        {/* Schema Markup - Breadcrumb List */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.myomnamo.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Terms and Conditions",
                item: "https://www.myomnamo.com/terms-and-condition",
              },
            ],
          })}
        </script>

        {/* Schema Markup - WebPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "WebPage",
            name: "Terms and Conditions - Myomnamo - Spiritual Services",
            url: "https://www.myomnamo.com/terms-and-condition",
            description:
              "Read our Terms & Conditions for using Myomnamo services. Understand policies for pandit bookings, pujas, and other spiritual services.",
            publisher: {
              "@type": "Organization",
              name: "Myomnamo",
            },
          })}
        </script>
      </Helmet>
      <section>
        <div
          className="container mx-auto p-6 mt-4"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <h3 className="text-2xl font-bold mb-4">
            {t("Terms and Conditions for Myomnamo")}
          </h3>
          <p>
            {t(
              "Welcome to Myomnamo! These Terms and Conditions (Terms) govern your use of our website"
            )} (
            <a href="https://www.myomnamo.com" className="text-blue-500">
              www.myomnamo.com
            </a>
            ){" "}
            {t(
              "and the spiritual services we provide, including puja bookings, horoscope services, temple visits, and e-store purchases. By accessing or using our services, you agree to comply with these Terms. Please read them carefully."
            )}
          </p>

          <h4 className="text-xl font-semibold mt-6">
            {t("1. Acceptance of Terms")}
          </h4>
          <p>
            {t(
              "By using Myomnamo, you confirm that you have read, understood, and agreed to these Terms. If you do not agree, please refrain from using our services. We reserve the right to modify these Terms at any time, and your continued use will constitute acceptance of the updated terms."
            )}
          </p>

          <h4 className="text-xl font-semibold mt-6">{t("2. Services Offered")}</h4>
          <p>{t("Myomnamo provides online spiritual services, including:")}</p>
          <ul className="list-disc pl-6">
            <li>{t("Booking pandits for pujas and rituals")}</li>
            <li>{t("Daily puja services")}</li>
            <li>{t("Horoscope and astrology consultations")}</li>
            <li>{t("Temple visit and darshan bookings")}</li>
            <li>{t("Purchase of puja samagri and spiritual products from our e-store")}</li>
          </ul>
          <p>
            {t(
              "All services are subject to availability, and we reserve the right to modify or discontinue any service without prior notice."
            )}
          </p>

          <h4 className="text-xl font-semibold mt-6">
            {t("3. User Responsibilities")}
          </h4>
          <ul className="list-disc pl-6">
            <li>
              {t(
                "You must provide accurate and complete information when booking services or making purchases."
              )}
            </li>
            <li>{t("Payments must be made as per the specified rates.")}</li>
            <li>
              {t(
                "Any misuse, fraudulent activity, or violation of these Terms may result in service cancellation or legal action."
              )}
            </li>
            <li>
              {t("You agree not to use our services for any unlawful or unethical purposes.")}
            </li>
          </ul>

          <h4 className="text-xl font-semibold mt-6">
            {t("4. Booking and Cancellation Policy")}
          </h4>

          <h5 className="font-medium mt-4">{t("Puja and Ritual Bookings:")}</h5>
          <ul className="list-disc pl-6">
            <li>{t("Bookings are confirmed only after full payment.")}</li>
            <li>
              {t(
                "Cancellations must be made at least 48 hours in advance for a refund, if applicable."
              )}
            </li>
            <li>
              {t(
                "No refunds will be provided for cancellations made after the specified time or for no-shows."
              )}
            </li>
          </ul>

          <h5 className="font-medium mt-4">
            {t("Horoscope and Astrology Services:")}
          </h5>
          <ul className="list-disc pl-6">
            <li>{t("Consultations are based on the details provided by you.")}</li>
            <li>
              {t("Refunds, if applicable, are subject to the discretion of Myomnamo.")}
            </li>
          </ul>

          <h5 className="font-medium mt-4">{t("E-Store Purchases:")}</h5>
          <ul className="list-disc pl-6">
            <li>{t("Products are shipped as per availability.")}</li>
            <li>
              {t("Refunds or replacements are subject to our")}&nbsp;
              <a href="#" className="text-blue-500">
                {t("Return and Refund Policy")}
              </a>
              .
            </li>
          </ul>

          <h4 className="text-xl font-semibold mt-6">
            {t("5. Payments and Pricing")}
          </h4>
          <ul className="list-disc pl-6">
            <li>
              {t(
                "All transactions are processed securely through trusted payment gateways."
              )}
            </li>
            <li>{t("Prices are subject to change without notice.")}</li>
            <li>
              {t(
                "Additional charges (e.g., travel fees for pandits) may apply and will be communicated beforehand."
              )}
            </li>
          </ul>

          <h4 className="text-xl font-semibold mt-6">{t("6. Privacy Policy")}</h4>
          <p>
            {t("Your privacy is important to us. Our")}
            <a href="/privacy-policy" className="text-blue-500">
              &nbsp;{t("Privacy Policy")}&nbsp;
            </a>
            {t(
              "explains how we collect, use, and protect your personal information. By using our services, you consent to our data practices."
            )}
          </p>

          <h4 className="text-xl font-semibold mt-6">
            {t("7. Intellectual Property")}
          </h4>
          <p>
            {t(
              "All content on Myomnamo, including text, images, and logos, is our property and protected by copyright laws. Unauthorized use, copying, or distribution is prohibited."
            )}
          </p>

          <h4 className="text-xl font-semibold mt-6">
            {t("8. Limitation of Liability")}
          </h4>
          <ul className="list-disc pl-6">
            <li>
              {t(
                "Myomnamo acts as a facilitator between users and service providers (pandits, temples, etc.)."
              )}
            </li>
            <li>
              {t(
                "We do not guarantee the accuracy or outcomes of horoscope or puja services."
              )}
            </li>
            <li>
              {t(
                "We are not liable for any direct, indirect, or incidental damages arising from service use."
              )}
            </li>
          </ul>

          <h4 className="text-xl font-semibold mt-6">{t("9. Third-Party Links")}</h4>
          <p>
            {t(
              "Our website may contain links to external sites. We are not responsible for their content or practices."
            )}
          </p>

          <h4 className="text-xl font-semibold mt-6">
            {t("10. Governing Law and Dispute Resolution")}
          </h4>
          <p>
            {t("These Terms are governed by Indian law. Any disputes will be subject to the jurisdiction of courts in")}{" "}
            <strong>{t("Mumbai, Maharashtra")}</strong>.
          </p>

          <h4 className="text-xl font-semibold mt-6">{t("11. Contact Us")}</h4>
          <p>{t("If you have any questions, please contact us:")}</p>
          <p>
            <strong>{t("Email")}:</strong>{" "}
            <a href="mailto:info@myomnamo.com" className="text-blue-500">
              info@myomnamo.com
            </a>
          </p>
          <p>
            <strong>{t("Phone")}:</strong> +91 8879222277
          </p>

          <p className="mt-6">
            {t(
              "By using Myomnamo, you acknowledge that you have read, understood, and agreed to these Terms and Conditions."
            )}
          </p>

          <p className="font-semibold mt-6">
            {t("Thank you for choosing Myomnamo for your spiritual journey.")}
          </p>
        </div>
      </section>

    </>
  );
};

export default TermsAndConditionPage;
